import { useState } from 'react';
import { toast } from 'sonner';
import { Archive, Hourglass, RefreshCw } from 'lucide-react';
import type { JobRole } from '@hcc/types';
import { ApiError } from '../../lib/api';
import { useArchiveRole, useSapRoleSync } from '../../hooks/useRoles';
import { Card, CardHeader, CardSection } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';

/**
 * Roles awaiting creation in SAP: added by HR free-typing in the
 * new-employee wizard (IT gets an email at the time). "Check SAP" re-runs
 * the sync; if the job now exists under this name the role is adopted and
 * moves into the main list below.
 */
export function PendingSapRolesCard({ pending }: { pending: JobRole[] }) {
  const sync = useSapRoleSync();
  const archive = useArchiveRole();
  const [checkingId, setCheckingId] = useState<string | null>(null);

  async function check(role: JobRole) {
    setCheckingId(role.id);
    try {
      const res = await sync.mutateAsync();
      const adopted = res.summary.adopted.some(
        (name) => name.toLowerCase() === role.name.trim().toLowerCase(),
      );
      if (adopted) {
        toast.success(`"${role.name}" has been created in SAP and is now linked.`);
      } else {
        toast.info(`"${role.name}" is not in SAP yet.`);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'SAP check failed');
    }
    setCheckingId(null);
  }

  if (pending.length === 0) return null;

  return (
    <Card>
      <CardHeader
        icon={<Hourglass className="h-4 w-4" />}
        title="Pending creation in SAP"
        description="Added here (usually from the new employee wizard) but not in SAP yet. Create the job in SAP, then check."
      />
      <CardSection className="divide-y divide-divider/10 p-0">
        {pending.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 px-5 py-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-fg">{r.name}</div>
              <div className="text-xs text-fg-dim">
                added {new Date(r.created_at).toLocaleDateString()}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<RefreshCw className="h-4 w-4" />}
                onClick={() => check(r)}
                loading={sync.isPending && checkingId === r.id}
                disabled={sync.isPending}
              >
                Check SAP
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<Archive className="h-4 w-4" />}
                onClick={async () => {
                  try {
                    await archive.mutateAsync(r.id);
                    toast.success(`"${r.name}" archived`);
                  } catch (err) {
                    toast.error(err instanceof ApiError ? err.message : 'Could not archive');
                  }
                }}
              >
                Archive
              </Button>
            </div>
          </div>
        ))}
      </CardSection>
    </Card>
  );
}
